<template>
  <popup-modal ref="popup">
    <div class="messageBox verify-email-modal verify-signup-modal">
      <div class="modal-header modal-header-text"><h4 class="modal-title new-header-text">{{ t('950') }}</h4></div>
      <form @submit.prevent="verify" novalidate>
        <div class="modal-body modal-body-text">
          <div class="modal-body-text-div" v-html="t(restricted ? 'signup.instruction' : '951')"></div>
          <div class="modal-body-text-div email-text"><span>{{ restricted ? '\u00a0' : emailAddress }}</span></div>
          <div class="modal-input" :aria-busy="busy">
            <template v-for="(_, index) in codes" :key="index">
              <span v-if="restricted && index === 4" class="code-separator" aria-hidden="true">-</span>
              <input :id="'code-element-' + (index + 1)" :value="codes[index]"
                :aria-label="t('transfer.digit', [index + 1])" :aria-invalid="invalidIndex === index"
                :data-title="t('transfer.validCode')" data-toggle="tooltip" data-placement="top"
                :class="['verify-email-input', { error: invalidIndex === index }]"
                inputmode="numeric" pattern="[0-9]" maxlength="1" type="text" autocomplete="off"
                @input="advance(index, $event)" @paste.prevent="paste($event)" @keydown="keyDown(index, $event)"
                @focus="$event.target.select()" :disabled="busy || terminal" />
            </template>
          </div>
          <div v-if="restricted" class="transfer-countdown" role="status" :data-signup-state="displayState">{{ message }}</div>
        </div>
        <div class="modal-footer">
          <div class="button-container"><button id="verify-email-button" class="btn btn-default btn-truckscience btn-primary" type="submit" :disabled="busy || terminal"><span>{{ t('952') }}</span></button></div>
          <div class="message-container" @click="supportClick"><span v-html="supportText"></span></div>
          <div class="get-started-background"></div>
        </div>
      </form>
    </div>
  </popup-modal>
</template>
<script>
import config from '@/services/config';
import PopupModal from './PopupModal.vue';
import { ref, toRef, computed, onUnmounted, nextTick } from 'vue';
export default {
  name: 'VerifyEmailModal', components: { PopupModal }, props: { propsForModal: Object },
  setup(props) {
    const opts = toRef(props, 'propsForModal');
    const restricted = !!opts.value.pendingSignup, emailAddress = opts.value.emailAddress || '';
    const popup = ref(null), codes = ref(Array(restricted ? 8 : 4).fill('')), operating = ref(false), subscribedBusy = ref(false), invalidIndex = ref(-1);
    const busy = computed(() => operating.value || subscribedBusy.value);
    if (!restricted && /^[0-9]{4}$/.test(opts.value.optionalCode || '')) codes.value = Array.from(opts.value.optionalCode);
    const pending = ref(opts.value.pendingSignup), now = ref(Date.now()), feedback = ref(null), queuedResend = ref(false);
    const nextSend = ref(restricted ? Date.now() + pending.value.RetryAfterSeconds * 1000 : 0);
    let timer, unsubscribe, promise, resolveModal, settled = false;
    const t = config.getTranslationText;
    const remaining = computed(() => restricted ? Math.max(0, Math.ceil((Date.parse(pending.value.ExpiresUtc) - now.value) / 1000)) : 0);
    const retryIn = computed(() => Math.max(0, Math.ceil((nextSend.value - now.value) / 1000)));
    const terminal = computed(() => restricted && (remaining.value === 0 || !['pending', 'throttled'].includes(pending.value.State)));
    const displayState = computed(() => restricted && remaining.value === 0 ? 'expired' : (terminal.value ? pending.value.State : (feedback.value || 'pending')));
    const message = computed(() => t(displayState.value === 'expired' ? 'transfer.expiryLine' :
      terminal.value || feedback.value === 'failed' ? 'transfer.failureLine' : 'transfer.countdown',
      [Math.floor(remaining.value / 60) + ':' + String(remaining.value % 60).padStart(2, '0')]));
    const supportText = computed(() => (restricted ? '<a href="#" id="cancelSignup" class="verify-modal-a">' + t('65') + '</a>. ' : '') + t('953').replaceAll('<a id=', '<a href="#" id='));
    const input = index => document.getElementById('code-element-' + (index + 1));
    function clearInvalid() {
      if (invalidIndex.value >= 0) window.$(input(invalidIndex.value)).tooltip('destroy');
      invalidIndex.value = -1;
    }
    function invalid(index) {
      clearInvalid(); invalidIndex.value = index;
      nextTick(() => { if (!settled && invalidIndex.value === index) window.$(input(index)).tooltip({ trigger: 'manual', html: false }).tooltip('show'); });
      input(index)?.focus();
    }
    function receive(update) {
      if (settled || !update) return;
      subscribedBusy.value = update.busy === true;
      if (update.outcome) { settle(update.outcome); return; }
      if (update.error?.cancelled) { settle(update.error); return; }
      if (update.pendingSignup) {
        if (pending.value && !['pending', 'throttled'].includes(pending.value.State) && ['pending', 'throttled'].includes(update.pendingSignup.State)) return;
        pending.value = update.pendingSignup;
        if (update.timing !== false) nextSend.value = Date.now() + pending.value.RetryAfterSeconds * 1000;
      }
      if (update.error) {
        const state = update.error.signupState;
        if (state === 'stale' || state === 'expired') pending.value = { ...pending.value, State: state };
        feedback.value = 'failed'; queuedResend.value = false;
      } else feedback.value = null;
      now.value = Date.now();
      if (terminal.value) queuedResend.value = false;
      if (!busy.value) nextTick(drainResend);
    }
    function settle(result) {
      if (settled) return;
      settled = true; queuedResend.value = false; clearInterval(timer); clearInvalid(); unsubscribe?.(); popup.value?.close(); resolveModal?.(result);
    }
    function show() {
      if (promise) return promise;
      promise = new Promise(resolve => { resolveModal = resolve; });
      popup.value.open(); nextTick(() => { if (!settled) input(0)?.focus(); });
      if (restricted) {
        const stop = opts.value.subscribeSignup?.(receive);
        if (settled) { stop?.(); return promise; }
        unsubscribe = stop;
        timer = setInterval(() => { now.value = Date.now(); if (terminal.value) queuedResend.value = false; drainResend(); }, 250);
      }
      return promise;
    }
    onUnmounted(() => settle({ cancelled: true, unmounted: true }));
    function advance(index, event) {
      if (busy.value || terminal.value || settled) return;
      clearInvalid(); const value = event.target.value;
      if (value.length > 1 && fillCode(value)) return;
      codes.value[index] = value.replace(/[^0-9]/g, '').slice(-1); event.target.value = codes.value[index];
      if (codes.value[index]) (input(index + 1) || document.getElementById('verify-email-button'))?.focus();
    }
    function fillCode(value) {
      const digits = value.replace(/[\s\-–—]/g, '');
      if (!(new RegExp('^[0-9]{' + codes.value.length + '}$')).test(digits)) return false;
      clearInvalid(); codes.value = Array.from(digits); document.getElementById('verify-email-button')?.focus(); return true;
    }
    function paste(event) {
      if (busy.value || terminal.value || settled) return;
      if (!fillCode(event.clipboardData.getData('text'))) invalid(Math.max(0, codes.value.findIndex(c => !c)));
    }
    function keyDown(index, event) {
      if (busy.value || terminal.value || settled) return;
      if (event.key === 'Backspace') {
        clearInvalid(); if (!codes.value[index] && index > 0) { event.preventDefault(); codes.value[index - 1] = ''; input(index - 1)?.focus(); }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault(); input(index + (event.key === 'ArrowLeft' ? -1 : 1))?.focus();
      }
    }
    async function run(action, code) {
      if (busy.value || settled || (terminal.value && !['cancel', 'startAgain'].includes(action))) return;
      if (action === 'cancel' || action === 'startAgain') queuedResend.value = false;
      operating.value = true; clearInvalid();
      try {
        const result = await opts.value.operateSignup(action, code);
        if (settled) return;
        if (result.completed || result.cancelled) { settle(result); return; }
        if (result.pendingSignup) {
          receive({ pendingSignup: result.pendingSignup });
          if (action === 'resend' && result.pendingSignup.State === 'throttled') {
            queuedResend.value = true;
            // A zero-delay throttle is valid wire data, not permission to spin
            // through automatic sends in the same event-loop turn.
            nextSend.value = Math.max(nextSend.value, Date.now() + 1000);
          }
          if (action === 'resend' && result.pendingSignup.State === 'pending') codes.value.fill('');
          if (action === 'verify' && !terminal.value && result.pendingSignup.State === 'pending') invalid(0);
        } else receive({ error: { signupState: 'failed' } });
      } catch (error) {
        if (error?.signupBusy) return;
        if (error?.cancelled) settle(error); else receive({ error });
      }
      finally { operating.value = false; drainResend(); }
    }
    function verify() {
      if (busy.value || terminal.value || settled) return;
      const code = codes.value.join('');
      if (!(restricted ? /^[0-9]{8}$/ : /^[0-9]{4}$/).test(code)) { invalid(Math.max(0, codes.value.findIndex(c => !c))); return; }
      if (restricted) return run('verify', code);
      const result = { Action: 'verify', Code: code }; settle(result);
    }
    function drainResend() {
      if (!queuedResend.value || busy.value || settled || terminal.value || feedback.value === 'failed' || retryIn.value > 0) return;
      queuedResend.value = false; return run('resend');
    }
    function resendEmail() {
      if (restricted) {
        if (settled || terminal.value || feedback.value === 'failed' || queuedResend.value) return;
        queuedResend.value = true; return drainResend();
      }
      const result = { Action: 'resendEmail' }; settle(result);
    }
    function startAgain() {
      queuedResend.value = false;
      if (restricted) return run('startAgain');
      const result = { Action: 'startAgain' }; settle(result);
    }
    function supportClick(event) {
      const id = event.target.closest('a')?.id;
      if (!['resendEmail', 'startAgain', 'cancelSignup'].includes(id)) return;
      event.preventDefault();
      if (id === 'resendEmail') return resendEmail();
      if (busy.value) return;
      if (id === 'startAgain') return startAgain();
      queuedResend.value = false; return run('cancel');
    }
    return { show, popup, codes, restricted, emailAddress, busy, terminal, remaining, retryIn, message, displayState, supportText, invalidIndex,
      queuedResend, advance, paste, keyDown, run, verify, resendEmail, startAgain, supportClick, t };
  }
};
</script>
